import { useFlags } from "launchdarkly-react-client-sdk";
import PropTypes from "prop-types";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Link, Redirect } from "react-router-dom";
import { Form, Input, Segment } from "semantic-ui-react";
import useIsAdmin from "../../common/hooks/useIsAdmin";
import AppNotification from "../../common/notification-banner/app-notification";
import ProductPermissionsGrid from "../components/product-permissions-grid";
import PermissionsContext from "./context";

/**
 * Generates a close icon configuration object for use in UI components.
 * @param {function} callback - The function to be executed when the close icon is clicked.
 */
const getCloseIcon = (callback) => {
  return {
    name: "close",
    link: true,
    id: "clear_search_input",
    onClick: callback,
  };
};

const ProductCatalogPermissionsPage = ({ history }) => {
  const intl = useIntl();
  const { whitelabelRoiPixelEnabled } = useFlags();
  const [searchQuery, setSearchQuery] = useState("");
  const [successDeleteMessage, setSuccessDeleteMessage] = useState(null);
  const [errorDeleteMessage, setErrorDeleteMessage] = useState(null);

  const userIsAdmin = useIsAdmin();

  const permissionsContextValues = useMemo(
    () => ({
      setSuccessDeleteMessage,
      setErrorDeleteMessage,
      successDeleteMessage,
      errorDeleteMessage,
      searchQuery,
    }),
    [successDeleteMessage, errorDeleteMessage, searchQuery],
  );

  if (!(userIsAdmin || whitelabelRoiPixelEnabled)) {
    return <Redirect to="/campaigns" />;
  }

  const details = history.location?.state || {};

  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <PermissionsContext.Provider value={permissionsContextValues}>
      <Segment
        basic
        style={{ padding: "0" }}
      >
        <AppNotification>
          {details?.action === "created" &&
            intl.formatMessage({
              id: "MESSAGE_PERMISSION_CREATED",
              defaultMessage: "Permission successfully created",
            })}
        </AppNotification>
        <AppNotification>
          {details?.action === "updated" &&
            intl.formatMessage({
              id: "MESSAGE_PERMISSION_UPDATED",
              defaultMessage: "Permission successfully updated",
            })}
        </AppNotification>
        <AppNotification onDismiss={() => setSuccessDeleteMessage(null)}>{successDeleteMessage}</AppNotification>
        <AppNotification
          onDismiss={() => setErrorDeleteMessage(null)}
          isError
        >
          {errorDeleteMessage}
        </AppNotification>
        <h1>
          {intl.formatMessage({
            id: "HEADING_PRODUCT_CATALOG_PERMISSIONS",
            defaultMessage: "Product Catalog Permissions",
          })}
        </h1>
        <Segment
          basic
          style={{ padding: "0", display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Form
            autoComplete="off"
            noValidate
          >
            <Form.Field>
              <label>
                {intl.formatMessage({
                  id: "LABEL_AGENCIES",
                  defaultMessage: "Agencies",
                })}
              </label>
              <Input
                type="search"
                name="agency"
                data-testid="pcp-agency-search"
                onChange={(_e, { value }) => {
                  setSearchQuery(value);
                }}
                placeholder={intl.formatMessage({
                  id: "HINT_SEARCH_AGENCIES",
                  defaultMessage: "Search Agencies",
                })}
                icon={searchQuery.length ? getCloseIcon(clearSearch) : "search"}
                iconPosition={searchQuery.length ? null : "left"}
                value={searchQuery}
              />
            </Form.Field>
          </Form>
          <Link
            to="/admin/product-catalog-permissions/create"
            className="text__uppercase ui button compact primary"
            data-testid="pcp-add-link"
          >
            {intl.formatMessage({
              id: "BUTTON_ADD_PRODUCT_CATALOG_PERMISSION",
              defaultMessage: "Add Permission",
            })}
          </Link>
        </Segment>
        <ProductPermissionsGrid />
      </Segment>
    </PermissionsContext.Provider>
  );
};

export default ProductCatalogPermissionsPage;

ProductCatalogPermissionsPage.propTypes = {
  history: PropTypes.object.isRequired,
};
